import { PARAGRAPH_OR_SOFT_BREAK, SOFT_LINE_BREAK } from '@lite-workspace/text-layout';
import { CARET_BASELINE_DESCENT_RATIO } from './caret-metrics';
import type { TextFrameFit } from './glyph-layout-backend';

interface Point {
  x: number;
  y: number;
}

export interface Quad {
  tl: Point;
  tr: Point;
  br: Point;
  bl: Point;
}

export interface TextEditStyle {
  fontFamily: string;
  /** CSS `font-variation-settings` of the engine's axis values for the box font (`"wght" 700, "slnt" -10`). */
  fontVariationSettings?: string;
  /** Rendered font size in SCREEN px (book fontSize × stage.scale). */
  fontSizePx: number;
  lineHeightRatio: number;
  /** Letter spacing in SCREEN px. */
  letterSpacingPx: number;
  color: string;
  align: 'left' | 'center' | 'right';
  /** PSD fontCaps: 2 = all-caps (text-transform), 1 = small-caps — so the textarea glyph widths match the engine. */
  fontCaps?: number;
  /** Block mode wraps (white-space: pre-wrap); free mode never wraps (pre) — matches the engine's flow. */
  block?: boolean;
  /** PSD glyph stretch ("Масштаб глифов"); the engine condenses/expands the glyphs by these — Konva uses scale(h,v). */
  horizontalScale?: number;
  verticalScale?: number;
  /** First-line cap-height in SCREEN px, from the SAME source the engine draws from (resolveCapHeightRatio × fontSize
   *  × scale). The caret's vertical offset uses THIS, not a re-measured Canvas cap — else it drifts off the glyphs. */
  engineCapPx?: number;
  /** Engine's decorative-font first-line pull-up in SCREEN px (0 for most caps text). */
  ascenderDeltaPx?: number;
  /**
   * The box keeps Photoshop paragraphs (`textLayout` 7): Shift+Enter writes a soft return (U+2028, a line break
   * inside the paragraph) instead of a new paragraph, and a soft return ends a row of the click model.
   */
  softReturns?: boolean;
}

/** The engine's axis values as CSS `font-variation-settings`; `undefined` when there are none. */
export function cssFontVariationSettings(variation: Record<string, number> | undefined): string | undefined {
  const axes = Object.entries(variation ?? {}).filter(([tag, value]) => tag.length === 4 && Number.isFinite(value));
  return axes.length ? axes.map(([tag, value]) => `"${tag}" ${value}`).join(', ') : undefined;
}

// FontFace family the host registers (Konva `__canvas__` scheme) so the caret textarea uses the engine font, not a fallback.
export function editCssFontFamily(box: {
  postScriptName?: string;
  fontName: string;
  fontWeight?: string;
  italic?: boolean;
}): string {
  const ps = box.postScriptName || box.fontName;
  return `__canvas__${ps}__${box.fontWeight || 'Regular'}${box.italic ? '_italic' : ''}`;
}

/** Caret geometry in the box's LOCAL px (origin = frame top-left, before the box matrix). */
export interface ShapedCaretGeometry {
  x: number;
  top: number;
  height: number;
  /** Clockwise turn (rad) about the top point — a glyph on a path stands at its tangent; absent = upright. */
  angle?: number;
}

/** A highlight band: a box turned about its top-left. `outline` (clockwise from top-left, inside the unturned box)
 *  cuts it to a warped line's bend. */
export interface ShapedCaretBand {
  x: number;
  top: number;
  width: number;
  height: number;
  angle?: number;
  outline?: ReadonlyArray<{ x: number; y: number }>;
}

/**
 * Index ↔ position mapping over the ENGINE's own line grid, for text the browser cannot lay out the same way
 * (Area Type on Shape: the engine flows the lines along the contour bands, the textarea along the rectangle).
 */
export interface ShapedCaretLayout {
  slotAt(index: number): ShapedCaretGeometry | undefined;
  indexAt(localX: number, localY: number): number;
  indexByLineStep(index: number, delta: -1 | 1, desiredX?: number): number | undefined;
  lineEdge(index: number, edge: 'start' | 'end'): number;
  /** Highlight bands of `[from; to)` on the engine's line grid — the browser's own highlight is suppressed. */
  bandsFor(from: number, to: number): ShapedCaretBand[];
  /** Polygons (local px) where a click still belongs to the text; a fixed surface takes clicks only there. */
  hitArea?: ReadonlyArray<ReadonlyArray<{ x: number; y: number }>>;
}

/**
 * Builds the caret grid for the CURRENT text (it is re-wrapped on every keystroke). `undefined` = the browser
 * lays this box out the same way the engine does, so it keeps owning the caret exactly as before.
 */
export type ShapedCaretSource = (text: string) => ShapedCaretLayout | undefined;

export interface TextEditEnterParams {
  id: string;
  text: string;
  quad: Quad;
  /** The box's UNSTRETCHED width/height in screen px (book dims × stage.scale) — the local size the matrix scales. */
  localWidth: number;
  localHeight: number;
  style: TextEditStyle;
  /** Area-shape boxes only: the engine's caret grid. Absent → the browser's own textarea caret (unchanged path). */
  shapedCaret?: ShapedCaretSource;
  /** Surface pinned to the whole frame: text on a path has no line box to follow, yet every click on it must land. */
  fixedSurface?: boolean;
  /** Where the session was opened, in client px — the caret starts under the pointer. */
  caretAt?: { clientX: number; clientY: number };
  /** How the session was opened: a double-click selects the word under the pointer, like any editor. */
  entry?: 'click' | 'dblclick';
  /** Rejects input that would push the laid-out text past the box frame (the frame is the maximum).
   *  Deletions and pre-existing overflow stay allowed — only growth past the frame is blocked. */
  clampToFrame?: boolean;
  /** How far a candidate string would spill past the frame, measured by the RENDERER's layout (the host wires this to
   *  the same pass that positions the drawn glyphs). The clamp's only source of truth — without it there is no clamp,
   *  because a fit re-derived from this textarea's own box drifts from what the engine actually draws. */
  measureFit?: (text: string) => TextFrameFit;
  /** A frame with its own character capacity (vignette series quote, R10): how many characters `text` runs past it.
   *  Present → the clamp caps by this count instead of the frame edge (the text may run past the compact frame while
   *  typing; the planner hugs the frame to the committed text). `measureFit` still shapes the caret. */
  overCapacity?: (text: string) => number;
  /** Screen px the ENGINE pushes the first line down for `verticalAlign` (0 for top-aligned text). The caret surface
   *  is laid out from the frame's top, so without this it — and the text it carries — sit above the drawn glyphs. */
  verticalShift?: (text: string) => number;
  /** Product rules for THIS box (student quote): cap, Enter-saves, counter chip. Absent → unchanged behaviour. */
  rules?: TextEditRules;
  /** How many FRAME widths the engine's longest laid-out line occupies. The browser measures the surface's own text
   *  with the CSS font, which diverges from the engine's shaping (tracking, caps, glyph stretch), so a centred line
   *  wider than its frame ended up a different length than the drawn one and the selection covered the wrong span. */
  contentWidthRatio?: (text: string) => number;
  /** Phone dock: the field mounts here as an opaque, static input instead of a caret surface over the glyphs. The
   *  session (handlers, cap, clamp, IME, blur commit, chip) is the same; only the seat differs. */
  dockHost?: HTMLElement;
}

/**
 * Product rules a host puts on ONE box (student quote): a hard character cap, Enter as the save, and the
 * «Осталось N символов» chip under the box. Absent → the session behaves exactly as it always did.
 */
export interface TextEditRules {
  maxLength: number | null;
  commitOnEnter: boolean;
  counter: 'remaining' | null;
}

export interface TextEditHandlers {
  onCommit: (id: string, text: string) => void;
  onCancel?: (id: string) => void;
  onEnd?: (id: string) => void;
  /** Живая каретка/выделение сессии — панель цвета следит за буквой перед курсором (Word/PS-поведение). */
  onSelectionChange?: (id: string, selection: { start: number; end: number }) => void;
  /** Live text on each keystroke — the host re-renders the engine glyphs (the textarea itself is caret-only). */
  onInput?: (id: string, text: string) => void;
}

const round = (n: number): number => Math.round(n * 1e6) / 1e6;

/** CSS rotation suffix; upright elements keep their transform string as it always was. */
const turn = (angle: number): string => (angle ? ` rotate(${round(angle)}rad)` : '');

/**
 * CSS transform + size mapping a textarea sized to the box's UNSTRETCHED local dims onto the screen-px quad.
 * The linear part is recovered by dividing the quad edges by the local dims (NOT by edge length) — so the box's
 * real scale/shear (e.g. a condensed PSD transform scaleX≠1) is applied to the GLYPHS, exactly like the engine,
 * instead of being absorbed into the width (which would leave the caret drifting). Full `matrix()` — no decompose.
 *
 * `hScale`/`vScale` are the PSD per-glyph stretch the engine bakes into the glyph advances (`glyph-layout-backend`
 * `× hScale`/`× vScale`). They scale the linear axes and inversely grow the textarea's local size, so the box
 * outline stays put while the glyph advances (and block-wrap width) condense exactly like the engine — without
 * which the browser lays the text wider than the rendered glyphs and the caret drifts toward the line centre.
 */
export function textareaTransformFromQuad(
  q: Quad,
  localWidth: number,
  localHeight: number,
  topOffsetPx = 0,
  hScale = 1,
  vScale = 1,
): {
  width: number;
  height: number;
  transform: string;
  transformOrigin: string;
  matrix: number[];
} {
  const a = (localWidth ? (q.tr.x - q.tl.x) / localWidth : 1) * hScale;
  const b = (localWidth ? (q.tr.y - q.tl.y) / localWidth : 0) * hScale;
  const c = (localHeight ? (q.bl.x - q.tl.x) / localHeight : 0) * vScale;
  const d = (localHeight ? (q.bl.y - q.tl.y) / localHeight : 1) * vScale;
  // Shift the textarea up along the box's local vertical axis (unit of (c,d)) so the caret lands on the cap-line.
  const ln = Math.hypot(c, d) || 1;
  const tx = q.tl.x - (topOffsetPx * c) / ln;
  const ty = q.tl.y - (topOffsetPx * d) / ln;
  const matrix = [a, b, c, d, tx, ty].map(round);
  const width = hScale ? localWidth / hScale : localWidth;
  const height = vScale ? localHeight / vScale : localHeight;
  return { width, height, transform: `matrix(${matrix.join(', ')})`, transformOrigin: '0 0', matrix };
}

/**
 * Frame-local pointer → the caret surface's own space. The bare-quad caret mapping knows nothing about how the
 * surface was SEATED: the vertical-align shift rides the matrix as `topOffset` (negative on a centred/bottom
 * layer), the glyph stretch scales the x-axis, and a grown right/centre surface slid left by `shiftX`. Feeding
 * the raw frame point into the row/column math made the opening click resolve to the LAST line of a centred
 * multi-line layer — and the double-click then selected a word the designer never aimed at.
 */
export function surfacePointFromFrame(
  local: { x: number; y: number },
  topOffset: number,
  horizontalScale: number,
  shiftX: number,
  verticalScale = 1,
): { x: number; y: number } {
  // The seat shift rides the matrix in frame px, so it joins BEFORE the vertical division into surface rows.
  return { x: local.x / (horizontalScale || 1) - shiftX, y: (local.y + topOffset) / (verticalScale || 1) };
}

/**
 * Width for the caret surface of FREE (point) text once the content outgrows the frozen frame. The engine
 * anchors the line at the alignment edge (right/center lines grow LEFT), while a fixed-width textarea with
 * overflow:hidden clips exactly that overflow — the caret vanishes mid-typing. The textarea must instead grow
 * to the content and slide along its local x-axis so the alignment edge stays glued to the frame's:
 * shiftX ≤ 0 in textarea-local px (right: the whole overflow; center: half; left: none). Never narrower than
 * the frame — short text keeps the original geometry byte-identical.
 */
export function grownWidthLayout(
  align: string | undefined,
  baseWidth: number,
  contentWidth: number,
): { width: number; shiftX: number } {
  const width = Math.max(baseWidth, contentWidth);
  const shiftX = align === 'right' ? baseWidth - width : align === 'center' ? (baseWidth - width) / 2 : 0;
  return { width, shiftX };
}

/**
 * Pure vertical-offset math: how far to shift the textarea UP so its caret lands on the engine glyphs.
 * `ascentPx`/`descentPx` are the BROWSER line-box metrics (where Blink puts the baseline in the textarea);
 * `capHeightPx` is the ENGINE cap-height (where the engine draws the glyphs) — the two must come from their
 * respective owners, never a single re-measured value, or the caret drifts. `ascenderDeltaPx` is the engine's
 * decorative-font pull-up. Testable without a canvas.
 */
export function firstLineOffsetPx(
  lineHeightPx: number,
  ascentPx: number,
  descentPx: number,
  capHeightPx: number,
  ascenderDeltaPx = 0,
): number {
  return lineHeightPx / 2 + (ascentPx - descentPx) / 2 - capHeightPx + ascenderDeltaPx;
}

/**
 * How far to shift the textarea UP so its (visible) caret coincides with the engine-rendered glyphs. Browser
 * line-box ascent/descent come from Canvas2D (what Blink lays the textarea to) — or, for a variable file's instance
 * (`fontVariationSettings` other than `normal`), from a hidden DOM probe at those axes (`lineBoxAtAxes`); the
 * cap-height comes from the ENGINE via `engineCapPx` (SSoT with `resolveCapHeightRatio`). When `engineCapPx` is
 * absent (no engine metric plumbed) it falls back to `max(cap('H'), cap('0'))` — still the engine's H/0 max, not a
 * bare 'H'. Returns 0 when browser metrics are unavailable (e.g. jsdom).
 */
// One measuring context for every offset probe — null retries, so a jsdom run never pins a dead cache.
let firstLineMeasureCtx: CanvasRenderingContext2D | null = null;

export function measureFirstLineOffset(
  fontFamily: string,
  fontSizePx: number,
  lineHeightPx: number,
  engineCapPx?: number,
  ascenderDeltaPx = 0,
  fontVariationSettings?: string,
): number {
  try {
    const ctx = (firstLineMeasureCtx ??= document.createElement('canvas').getContext('2d'));
    if (ctx) ctx.font = `${fontSizePx}px ${fontFamily}`;
    const all = ctx?.measureText('Hg');
    const { ascent, descent } =
      fontVariationSettings && fontVariationSettings !== 'normal'
        ? lineBoxAtAxes(fontFamily, fontSizePx, fontVariationSettings)
        : { ascent: all?.fontBoundingBoxAscent, descent: all?.fontBoundingBoxDescent ?? 0 };
    if (!ascent) return 0;
    const capHeight =
      engineCapPx ??
      (ctx ? Math.max(ctx.measureText('H').actualBoundingBoxAscent, ctx.measureText('0').actualBoundingBoxAscent) : undefined);
    if (capHeight === undefined) return 0;
    return firstLineOffsetPx(lineHeightPx, ascent, descent, capHeight, ascenderDeltaPx);
  } catch {
    return 0;
  }
}

/** `measureFirstLineOffset` for an edit session's style — the one call the opening and every line-count reshift share. */
const firstLineOffsetOf = (s: TextEditStyle, lineHeightPx: number): number =>
  measureFirstLineOffset(s.fontFamily, s.fontSizePx, lineHeightPx, s.engineCapPx, s.ascenderDeltaPx, s.fontVariationSettings);

/**
 * The line-box ascent/descent of `fontFamily` at the axes of `fontVariationSettings`, as the textarea lays its line
 * out. A canvas 2d context has no font-variation-settings: it measures the file's default instance, whose ascender
 * and descender an `MVAR` table can move off the instance's. An inline span's box is the content area the CSS line
 * box is built from — the font's ascent + descent, the same metrics Canvas2D's `fontBoundingBox*` reports for a font
 * without `MVAR` — and a zero-size inline-block beside it sits on the baseline, so the two rects split the box there.
 */
function lineBoxAtAxes(
  fontFamily: string,
  fontSizePx: number,
  fontVariationSettings: string,
): { ascent: number; descent: number } {
  const box = document.createElement('div');
  try {
    Object.assign(box.style, {
      position: 'absolute',
      left: '0px',
      top: '0px',
      visibility: 'hidden',
      whiteSpace: 'pre',
      lineHeight: 'normal',
      fontFamily,
      fontSize: `${fontSizePx}px`,
      fontVariationSettings,
    });
    const text = box.appendChild(document.createElement('span'));
    text.textContent = 'Hg';
    const baseline = box.appendChild(document.createElement('span'));
    Object.assign(baseline.style, { display: 'inline-block', width: '0px', height: '0px', verticalAlign: 'baseline' });
    document.body.appendChild(box);
    const line = text.getBoundingClientRect();
    const at = baseline.getBoundingClientRect().top;
    return { ascent: at - line.top, descent: line.bottom - at };
  } finally {
    box.remove();
  }
}

const SCROLL_PIN_EVENTS = ['scroll', 'keydown', 'keyup', 'select', 'focus'] as const;

/** On-screen caret thickness (px) for the self-drawn shaped caret. */
const SHAPED_CARET_WIDTH_PX = 1.5;
const SHAPED_CARET_BLINK_MS = 530;
/** Полупрозрачный акцент дизайн-системы — тот же, которым оверлей ведёт свои линии. */
const SHAPED_BAND_COLOR = 'var(--acc-line)';
const SHAPED_TEXTAREA_CLASS = 'psd-engine-grid-edit';
const PLAIN_TEXTAREA_CLASS = 'psd-engine-plain-edit';
/** id инжектируемого тега — отдельный от класса: иначе чужой элемент с таким id гасит инжект. */
const SHAPED_STYLE_ID = `${SHAPED_TEXTAREA_CLASS}-style`;

/**
 * Браузер красит подсветку выделения по СВОЕЙ раскладке и рисует выделенный текст своим цветом поверх
 * прозрачного — там, где сетки расходятся, и полоса, и текст уезжают. В сессиях на сетке движка гасим
 * и то и другое, а полосы кладём сами. На браузерной раскладке (плайн-путь) сетки совпадают, но дефолтная
 * подсветка поверх прозрачных глифов — сплошная тёмная заливка: перекрашиваем её в ту же акцентную
 * плашку, а выделенные глифы держим прозрачными — их рисует движок (FKS-11514).
 */
function ensureSelectionStyles(): void {
  if (typeof document === 'undefined' || document.getElementById(SHAPED_STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = SHAPED_STYLE_ID;
  const hidden = 'background:transparent;color:transparent';
  // Токен разворачиваем в литерал: var() внутри ::selection резолвится не во всех живых браузерах.
  const accent = getComputedStyle(document.documentElement).getPropertyValue('--acc-line').trim() || SHAPED_BAND_COLOR;
  const band = `background:${accent};color:transparent`;
  style.textContent =
    `.${SHAPED_TEXTAREA_CLASS}::selection{${hidden}}` +
    `.${SHAPED_TEXTAREA_CLASS}::-moz-selection{${hidden}}` +
    `.${PLAIN_TEXTAREA_CLASS}::selection{${band}}` +
    `.${PLAIN_TEXTAREA_CLASS}::-moz-selection{${band}}`;
  document.head.appendChild(style);
}

const WORD_CHAR = /[\p{L}\p{N}_]/u;

/** Authored lines only — the engine's vertical shift follows the laid-out block, and wrapping is the caller's business. */
function countLines(text: string, softReturns = false): number {
  return text.split(softReturns ? PARAGRAPH_OR_SOFT_BREAK : '\n').length;
}

/** Double-click word boundaries (browser parity): a word run around the index, else the single clicked char. */
function wordRangeAt(text: string, index: number): [number, number] {
  const isWord = (i: number): boolean => i >= 0 && i < text.length && WORD_CHAR.test(text[i]);
  let from = index;
  let to = index;
  if (isWord(index) || isWord(index - 1)) {
    while (isWord(from - 1)) from -= 1;
    while (isWord(to)) to += 1;
  } else if (index < text.length) {
    to = index + 1;
  }

  return [from, to];
}

/** Inverse of a CSS 2D matrix [a,b,c,d,e,f]; `undefined` for a degenerate (non-invertible) one. */
export function invertAffine(m: readonly number[]): number[] | undefined {
  const [a, b, c, d, e, f] = m;
  const det = a * d - b * c;
  if (!det) return undefined;
  return [d / det, -b / det, -c / det, a / det, (c * f - d * e) / det, (b * e - a * f) / det];
}

export function applyAffine(m: readonly number[], x: number, y: number): { x: number; y: number } {
  return { x: m[0] * x + m[2] * y + m[4], y: m[1] * x + m[3] * y + m[5] };
}

/**
 * Height for the caret-only textarea so its BOTTOM lands on the text baseline (+ a thin descent band), letting
 * overflow:hidden clip the caret/selection descent — the legacy SVG-editor trick that keeps the visible caret
 * spanning cap→baseline instead of the font's full (often inflated) glyph box. The box top is already shifted UP by
 * `topOffset`, so the baseline sits `topOffset + engineCapPx − ascenderDeltaPx` below it. Single-line only; block/
 * multiline (and the no-engine-metric case) keep the transform-derived `fallbackHeight` so the caret lands on the
 * right line.
 */
export function caretClipHeight(
  topOffset: number,
  fontSizePx: number,
  fallbackHeight: number,
  engineCapPx: number | undefined,
  ascenderDeltaPx = 0,
  block = false,
  lines = 1,
  lineHeightPx = 0,
  verticalScale = 1,
): number {
  if (block || engineCapPx === undefined) return fallbackHeight;
  // Down to the LAST line's baseline: clipping to the first line left every following line of a multi-line free
  // text outside the surface — a click there fell through to the canvas and closed the session instead of placing
  // the caret ("can't click into the second line of a duplicated ФИО"). The surface is drawn squeezed by the
  // vertical scale, while the engine keeps the leading and the top shift unscaled — those run in surface px ÷ scale.
  const v = verticalScale || 1;
  const lastBaseline = (topOffset + Math.max(0, lines - 1) * lineHeightPx) / v + engineCapPx - ascenderDeltaPx;
  return lastBaseline + fontSizePx * CARET_BASELINE_DESCENT_RATIO;
}

/**
 * Character index at `targetX` px inside `line`, judged by the halfway point of each glyph's advance — the browser's
 * own caret rule. `measure` must speak the SURFACE's font (family, size, letter-spacing, caps), so the answer lands
 * on the same glyph the browser draws the caret for.
 */
export function indexAtMeasuredX(line: string, targetX: number, measure: (prefix: string) => number): number {
  if (targetX <= 0) return 0;
  let prev = 0;
  for (let i = 1; i <= line.length; i++) {
    const adv = measure(line.slice(0, i));
    if (targetX < (prev + adv) / 2) return i - 1;
    prev = adv;
  }

  return line.length;
}

/**
 * The surface's own wrap, as visual rows with buffer offsets. A block textarea wraps (`pre-wrap` +
 * `break-word`) while the buffer keeps ONE authored line — resolving a click's row against authored newlines
 * put the caret rows apart from the rows the user sees. Greedy: break after the last space that fits, else
 * inside the word; `Infinity` wrap width degrades to the authored lines (free text never wraps).
 */
export function visualLinesOf(
  text: string,
  wrapWidth: number,
  measure: (prefix: string) => number,
  softReturns = false,
): Array<{ text: string; start: number }> {
  const out: Array<{ text: string; start: number }> = [];
  let offset = 0;
  // A soft return (`textLayout` 7) ends a row like a paragraph break; below it U+2028 is an ordinary char.
  for (const authored of text.split(softReturns ? PARAGRAPH_OR_SOFT_BREAK : '\n')) {
    let rest = authored;
    let start = offset;
    do {
      if (measure(rest) <= wrapWidth) {
        out.push({ text: rest, start });
        break;
      }

      // longest fitting prefix (monotonic advance → binary search), never zero: an over-wide glyph keeps its row
      let lo = 1;
      let hi = rest.length - 1;
      while (lo < hi) {
        const mid = (lo + hi + 1) >> 1;
        if (measure(rest.slice(0, mid)) <= wrapWidth) lo = mid;
        else hi = mid - 1;
      }

      const space = rest.lastIndexOf(' ', lo - 1);
      const breakAt = space > 0 ? space + 1 : lo;
      out.push({ text: rest.slice(0, breakAt), start });
      start += breakAt;
      rest = rest.slice(breakAt);
    } while (rest.length > 0);

    offset += authored.length + 1;
  }

  if (out.length === 0) out.push({ text: '', start: 0 });
  return out;
}

/**
 * Caret line box for a frame LOCKED to one line (free text, engine cap metric, clamp active so a newline can never
 * arrive): the CSS line-height is compressed to the visible caret span (cap→baseline + descent band), so the native
 * caret's TOP lands on the cap line instead of floating a half-leading above it — `overflow:hidden` clipping alone
 * only ever trims the BOTTOM of the caret, never its top. Everything else (block, multiline-capable frames, no
 * engine metric) keeps the REAL line height so wrapped/extra lines land exactly where the engine draws them.
 */
export function caretLineHeightPx(
  fontSizePx: number,
  realLineHeightPx: number,
  engineCapPx: number | undefined,
  ascenderDeltaPx = 0,
  singleLineLocked = false,
): number {
  if (!singleLineLocked || engineCapPx === undefined) return realLineHeightPx;
  return engineCapPx - ascenderDeltaPx + fontSizePx * CARET_BASELINE_DESCENT_RATIO;
}

/**
 * Konva-free inline text editor: a real DOM `<textarea>` positioned over a (possibly skewed) text box via a
 * CSS matrix. The browser owns caret/selection/copy-paste/IME inside it; the engine box is hidden by the host.
 * Commit-on-blur (one undo per session); IME composition defers the commit so no character is dropped.
 */
// Keeps the caret at a grown line's end from sitting exactly on the clipped edge.
const GROW_CARET_SLACK_PX = 2;

const CHIP_GAP_PX = 8;
const LIMIT_MESSAGE = 'Достигнут максимальный размер цитаты';
const LIMIT_MESSAGE_MS = 5000;

/** Russian plural for «символ» — the counter is read as a sentence, not as a number with a fixed tail. */
function charWord(n: number): string {
  const teen = n % 100;
  if (teen >= 11 && teen <= 14) return 'символов';
  const last = n % 10;
  if (last === 1) return 'символ';
  return last >= 2 && last <= 4 ? 'символа' : 'символов';
}

interface EditSnapshot {
  value: string;
  start: number;
  end: number;
}

const fitsFrame = (fit: TextFrameFit): boolean => fit.heightOver <= 0 && fit.widthOver <= 0;

/** How far an axis actually spills; inside the frame is 0, never a negative margin worth defending. */
const spill = (over: number): number => Math.max(0, over);

/** Самый длинный кусок вставки, который проходит `fits`: слова добираются по одному, конец — всегда по границе
 *  слова. Ни одно слово не подошло — пустая строка, вставлять нечего. */
function fittingPrefix(inserted: string, fits: (text: string) => boolean): string {
  let fitting = '';
  let taken = '';
  // Слово вместе с ведущими пробелами и переносами — кусок собирается ровно как в оригинале.
  for (const word of inserted.match(/\s*\S+/g) ?? []) {
    taken += word;
    if (!fits(taken)) break;
    fitting = taken;
  }

  return fitting;
}

export class OverlayTextEdit {
  private textarea?: HTMLTextAreaElement;
  private id?: string;
  private composing = false;
  private done = false;
  /** Выделение на момент конца сессии (blur/Escape) — панель красит его после того, как textarea уже мертва. */
  private endedSelection?: { start: number; end: number };
  private pendingCommit = false;
  private baseWidth = 0;
  private verticalShift?: (text: string) => number;
  private contentWidthRatio?: (text: string) => number;
  private shiftLineCount = 0;
  /** Lines as the engine LAYS them OUT (authored breaks + block wrap) — centring shifts move on wrap, not only on Enter. */
  private laidOutLines: (text: string) => number = countLines;
  /** One glyph layout per text value — the count is re-read on every input event. */
  private lineCountCache?: { text: string; lines: number };
  /** Session entered single-line-locked: the compressed caret line box is valid ONLY while the buffer holds one
   *  line — the metrics swap to the engine's real leading the moment Enter adds a second one, and back. */
  private entrySingleLineLocked = false;
  private entryLineHeightPx = 0;
  /** The engine ratio depends only on the TEXT — a camera re-seat re-runs growWidth per pan frame, and paying a
   *  full glyph layout (~15 ms on a long line) for an unchanged string would stutter the pan it rides. */
  private growRatioCache?: { text: string; ratio: number };
  /** The alignment slide growWidth last applied — the opening-click mapping must undo it. */
  private growShiftX = 0;
  /** `topOffset` before the engine's vertical-align shift — the shift is re-subtracted whenever the line count moves. */
  private baseTopOffset = 0;
  private placement?: {
    quad: Quad;
    localWidth: number;
    localHeight: number;
    topOffset: number;
    style: TextEditStyle;
    realLineHeightPx: number;
  };

  private baseMatrix: number[] = [1, 0, 0, 1, 0, 0];
  private align = 'left';
  private isBlock = false;
  private softReturns = false;
  private fixedSurface = false;
  /** Field seated in the host's dock, not over the glyphs: no quad seat, width growth or line reshift. */
  private dockHost?: HTMLElement;
  private initialText = '';
  // Engine-grid session only: the engine's caret grid + the DOM caret and highlight we draw from it (see enter()).
  private shapedSource?: ShapedCaretSource;
  private shapedLayout?: ShapedCaretLayout;
  private caretEl?: HTMLDivElement;
  /** Highlight bands drawn from the engine grid; the browser's own highlight is hidden in these sessions. */
  private bandEls: HTMLDivElement[] = [];
  /** Quad matrix WITHOUT the first-line shift and glyph stretch — the caret rides the box frame itself. */
  private caretMatrix: number[] = [1, 0, 0, 1, 0, 0];
  private caretBlinkTimer?: ReturnType<typeof setInterval>;
  /** Column the caret keeps while walking ↑/↓ across lines of different width (browser parity). */
  private caretGoalX?: number;
  /** Fixed end of a shaped selection (mouse drag / Shift-extend) — the browser's own anchor lives in its
   * rectangular layout and would land on other glyphs, so the gesture keeps its own engine-true anchor. */
  private selectionAnchor?: number;
  // Set only when the host supplied an engine fit probe — its presence IS the clamp being on.
  private measureFit?: (text: string) => TextFrameFit;
  /** One 2d context per editor instance — the click mapping re-measures per call, not per canvas. */
  private measureCtx?: CanvasRenderingContext2D | null;
  // Product rules of the live session (student quote) and the chip they draw under the box.
  private rules?: TextEditRules;
  private chipEl?: HTMLDivElement;
  private chipCounterEl?: HTMLSpanElement;
  private chipOkEl?: HTMLButtonElement;
  private chipMessageEl?: HTMLDivElement;
  private chipMessageTimer?: ReturnType<typeof setTimeout>;
  // Pre-mutation snapshots the clamp reverts to: per keystroke (beforeinput) and per IME session (compositionstart).
  private inputSnap: EditSnapshot = { value: '', start: 0, end: 0 };
  private compositionSnap: EditSnapshot = this.inputSnap;

  constructor(
    private readonly host: HTMLElement,
    private readonly handlers: TextEditHandlers,
  ) {}

  get active(): boolean {
    return !!this.textarea;
  }

  get editingId(): string | undefined {
    return this.id;
  }

  enter(params: TextEditEnterParams): void {
    if (this.textarea) this.cancel();
    this.endedSelection = undefined;
    const s = params.style;
    const realLineHeightPx = s.fontSizePx * s.lineHeightRatio;
    // No engine probe, no clamp: a fit re-derived from this textarea's own box is the divergence the probe replaces.
    // The probe itself is NOT clamp-only: it also answers whether this box can hold a second line, which is what
    // decides the caret's height below. An editor that measures but does not clamp (the design editor) still needs
    // a caret the height of its glyphs, not of the layer's leading.
    const overCapacity = params.overCapacity;
    const countFit =
      overCapacity && ((text: string): TextFrameFit => ({ heightOver: overCapacity(text), widthOver: 0, lines: 0 }));
    const measureFit = params.clampToFrame ? (countFit ?? params.measureFit) : undefined;
    const fitProbe = params.measureFit;
    // One-line frames under the clamp can never gain a newline, so the compressed caret line box is safe there. The
    // engine answers "would a second line still fit" — a line-count estimate off the frame height rounds, and a frame
    // that rounds up to two lines would compress the caret on a box that really holds only one. The question is
    // purely VERTICAL: a box entered with pre-existing width overflow is still tall enough for the line the user is
    // about to split off, and compressing its line box would misplace the caret on every line after the first.
    const wouldOverflowWithOneMoreLine = fitProbe ? fitProbe(`${params.text}\n`).heightOver > 0 : false;
    const singleLineLocked = !s.block && countLines(params.text, !!s.softReturns) === 1 && wouldOverflowWithOneMoreLine;
    const lineHeightPx = caretLineHeightPx(s.fontSizePx, realLineHeightPx, s.engineCapPx, s.ascenderDeltaPx, singleLineLocked);
    // Without the clamp the session CAN still gain a newline — the compressed line box must then hand the rows
    // back to the engine's real leading (reshiftIfLineCountChanged), or every line after the first sits off its glyphs.
    this.entrySingleLineLocked = singleLineLocked;
    this.entryLineHeightPx = lineHeightPx;
    // `topOffset` lifts the surface so its first BASELINE meets the engine's cap line, measured from the frame's TOP.
    // A layer with vertical align centre/bottom is not drawn from the top, so the engine's own shift comes off it —
    // otherwise the caret, and the text the surface carries, hover above the glyphs by exactly that much.
    this.verticalShift = params.verticalShift;
    this.contentWidthRatio = params.contentWidthRatio;
    this.lineCountCache = undefined;
    const soft = !!s.softReturns;
    const authoredLines = (text: string): number => countLines(text, soft);
    this.laidOutLines = fitProbe
      ? text => {
          if (this.lineCountCache?.text !== text) {
            // Authored breaks floor the probe's answer — a report of fewer (or 0 = unmeasured) never hides a real line.
            this.lineCountCache = { text, lines: Math.max(fitProbe(text).lines, authoredLines(text)) };
          }

          return this.lineCountCache.lines;
        }
      : authoredLines;
    this.shiftLineCount = s.block ? this.laidOutLines(params.text) : authoredLines(params.text);
    this.fixedSurface = !!params.fixedSurface && !!params.shapedCaret && !params.dockHost;
    this.baseTopOffset = this.fixedSurface ? 0 : firstLineOffsetOf(s, lineHeightPx);
    const topOffset = this.fixedSurface ? 0 : this.baseTopOffset - (params.verticalShift?.(params.text) ?? 0);
    this.placement = {
      quad: params.quad,
      localWidth: params.localWidth,
      localHeight: params.localHeight,
      topOffset,
      style: s,
      realLineHeightPx,
    };
    const { width, height, transform, transformOrigin, matrix } = textareaTransformFromQuad(
      params.quad,
      params.localWidth,
      params.localHeight,
      topOffset,
      s.horizontalScale ?? 1,
      s.verticalScale ?? 1,
    );
    this.baseWidth = width;
    this.baseMatrix = matrix;
    this.align = s.align;
    this.isBlock = !!s.block;
    this.softReturns = !!s.softReturns;
    // The caret frame is the bare quad: the textarea's own matrix carries the cap-line shift and the glyph
    // stretch, which the engine already baked into the glyph positions the shaped grid reports.
    this.caretMatrix = textareaTransformFromQuad(params.quad, params.localWidth, params.localHeight).matrix;
    this.dockHost = params.dockHost;
    // A docked field shows its own text: the browser's caret and highlight are the real ones, no engine grid.
    this.shapedSource = this.dockHost ? undefined : params.shapedCaret;
    this.shapedLayout = this.shapedSource?.(params.text);
    this.measureFit = measureFit;
    // Caret-height fix (legacy SVG-editor parity): a decorative font's glyph box is far taller than its caps, so the
    // browser draws the caret from the ascent, floating above the text; extend the textarea to the baseline and let
    // overflow:hidden clip the descent so the visible caret/selection spans cap→baseline (see caretClipHeight).
    // From the surface's OWN top, not from the shifted one: the vertical-align shift moved the whole surface down, it
    // did not move the text inside it, so measuring the clip off the shifted offset shortened the visible caret by
    // exactly that shift and pinned it to the cap line.
    const editHeight = this.fixedSurface
      ? height
      : caretClipHeight(
          this.baseTopOffset,
          s.fontSizePx,
          height,
          s.engineCapPx,
          s.ascenderDeltaPx,
          s.block,
          authoredLines(params.text),
          realLineHeightPx,
          s.verticalScale,
        );
    const ta = document.createElement('textarea');
    ta.rows = 1;
    ta.value = params.text;
    Object.assign(ta.style, {
      position: 'absolute',
      left: '0px',
      top: '0px',
      margin: '0px',
      border: '0px',
      padding: '0px',
      width: `${width}px`,
      height: `${editHeight}px`,
      // The font's own kerning (the engine uses opentype kerning) — the browser default often differs and drifts the caret.
      fontKerning: 'normal',
      transform,
      transformOrigin,
      fontFamily: s.fontFamily,
      fontVariationSettings: s.fontVariationSettings ?? 'normal',
      fontSize: `${s.fontSizePx}px`,
      lineHeight: `${lineHeightPx}px`,
      letterSpacing: `${s.letterSpacingPx}px`,
      // Caret-only: the glyphs are drawn by the engine (no browser-vs-engine layout jump). Only the caret shows.
      color: 'transparent',
      // Shaped (area) text: the browser's caret would sit on ITS rectangular line grid, not the engine's contour
      // bands — hide it and draw our own from the engine layout instead.
      caretColor: this.shapedLayout ? 'transparent' : s.color,
      textAlign: s.align,
      // PSD caps: the engine renders uppercase/small-caps, so the (caret-only) textarea must lay out the same
      // glyph widths — else the caret drifts by the upper/lowercase width difference (a multi-letter shift).
      ...(s.fontCaps === 2 ? { textTransform: 'uppercase' } : {}),
      ...(s.fontCaps === 1 ? { fontVariantCaps: 'small-caps' } : {}),
      background: 'transparent',
      outline: 'none',
      resize: 'none',
      overflow: 'hidden',
      // Free text never wraps (single flowing line); block text wraps within the box width — matches the engine.
      whiteSpace: s.block ? 'pre-wrap' : 'pre',
      wordBreak: s.block ? 'break-word' : 'normal',
      boxSizing: 'border-box',
      zIndex: '15',
    });
    if (this.dockHost) {
      // Opaque input in the dock: the host's field look (font family, colours) inherits; 16px keeps iOS from zooming.
      Object.assign(ta.style, {
        position: 'static',
        width: '100%',
        height: '',
        transform: 'none',
        fontFamily: 'inherit',
        fontVariationSettings: 'normal',
        fontSize: '16px',
        lineHeight: '1.4',
        letterSpacing: 'normal',
        // `currentcolor` on `color` IS the inherited colour (CSS Color 4) — and jsdom accepts it, unlike `inherit`.
        color: 'currentcolor',
        caretColor: 'auto',
        textAlign: 'left',
        textTransform: 'none',
        fontVariantCaps: 'normal',
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
        zIndex: '',
      });
    } else {
      ensureSelectionStyles();
      ta.classList.add(this.shapedLayout ? SHAPED_TEXTAREA_CLASS : PLAIN_TEXTAREA_CLASS);
    }

    this.composing = false;
    this.done = false;
    this.pendingCommit = false;
    this.growRatioCache = undefined; // per-session: the same string in another box has another ratio
    this.growShiftX = 0;
    this.initialText = params.text;
    this.rules = params.rules;
    // A quote saved before the cap existed stays SHORTENABLE: `maxlength` on an already-longer value would freeze
    // it at its own length, so the cap is then held by the refusal in `beforeinput` alone.
    const maxLength = params.rules?.maxLength ?? null;
    if (maxLength !== null && params.text.length <= maxLength) ta.maxLength = maxLength;
    // Fresh per-session snapshots — an input arriving without its beforeinput (legacy execCommand paths) must
    // revert to THIS session's text, never to a previous box's.
    this.inputSnap = { value: params.text, start: params.text.length, end: params.text.length };
    this.compositionSnap = this.inputSnap;
    this.id = params.id;
    this.textarea = ta;
    this.attachHandlers(ta);
    (this.dockHost ?? this.host).appendChild(ta);
    ta.focus();
    this.mountChip();
    if (this.dockHost) this.growHeight(); // rows=1 would hide the lines of a multi-line text until the first keystroke
    this.growWidth(); // the entered text may already outgrow the frame (imported overflow)
    if (this.shapedLayout) {
      this.mountShapedCaret(s.color);
      this.clipSurfaceToText();
      // The dblclick that opened the session never reached the textarea, so the browser put the caret at 0.
      const local = params.caretAt && this.clientToLocal(params.caretAt.clientX, params.caretAt.clientY);
      const index = local && this.shapedLayout.indexAt(local.x, local.y);
      if (index !== undefined && params.entry === 'dblclick') {
        const [from, to] = wordRangeAt(ta.value, index);
        this.selectShapedRange(from, to);
      } else if (index !== undefined) {
        this.setCaretIndex(index);
      }

      this.syncShapedCaret();
    } else if (params.caretAt) {
      // Plain text: the opening click also never reached the textarea — the caret sat at 0 whatever was clicked.
      // Map the pointer through the surface's own metrics; a double-click entry selects the word, editor-style.
      // clientToLocal yields FRAME-local coords (the shaped grid's space) — re-seat them into the surface's own.
      const local = this.clientToLocal(params.caretAt.clientX, params.caretAt.clientY);
      const surface = local
        ? surfacePointFromFrame(local, topOffset, s.horizontalScale ?? 1, this.growShiftX, s.verticalScale ?? 1)
        : undefined;
      const idx = surface ? this.plainIndexAt(ta, surface, lineHeightPx) : undefined;
      if (idx !== undefined) {
        if (params.entry === 'dblclick') {
          const [ws, we] = wordRangeAt(ta.value, idx);
          ta.setSelectionRange(ws, we);
        } else {
          ta.setSelectionRange(idx, idx);
        }
      }
    }
  }

  /** Index under a surface-local point, measured with the surface's own font — canvas metrics, per line. */
  private plainIndexAt(ta: HTMLTextAreaElement, local: { x: number; y: number }, lineHeightPx: number): number | undefined {
    let probe: HTMLSpanElement | undefined;
    try {
      const style = getComputedStyle(ta);
      const variation = ta.style.fontVariationSettings;
      const caps = ta.style.fontVariantCaps;
      let measureText: (text: string) => number;
      if ((variation && variation !== 'normal') || (caps && caps !== 'normal')) {
        // Canvas 2d has no font-variation-settings and Safari's has no fontVariantCaps: it would measure the default
        // instance at full-size glyphs — a hidden span in the surface's font measures what the textarea lays out.
        const span = (probe = document.createElement('span'));
        Object.assign(span.style, {
          position: 'absolute',
          left: '0px',
          top: '0px',
          visibility: 'hidden',
          whiteSpace: 'pre',
          fontFamily: style.fontFamily,
          fontSize: style.fontSize,
          fontKerning: 'normal',
          letterSpacing: style.letterSpacing,
          fontVariationSettings: variation || 'normal',
          fontVariantCaps: caps || 'normal',
        });
        document.body.appendChild(span);
        measureText = text => {
          span.textContent = text;
          return span.getBoundingClientRect().width;
        };
      } else {
        const ctx = (this.measureCtx ??= document.createElement('canvas').getContext('2d'));
        if (!ctx) return undefined;
        ctx.font = `${style.fontSize} ${style.fontFamily}`;
        const spacing = parseFloat(style.letterSpacing);
        const withSpacing = ctx as CanvasRenderingContext2D & { letterSpacing?: string };
        if (Number.isFinite(spacing) && withSpacing.letterSpacing !== undefined) withSpacing.letterSpacing = `${spacing}px`;
        measureText = text => ctx.measureText(text).width;
      }

      const upper = style.textTransform === 'uppercase';
      const measure = (prefix: string): number => measureText(upper ? prefix.toUpperCase() : prefix);
      const width = ta.clientWidth;
      // Block text wraps in the surface while the buffer keeps one authored line — the row model must speak
      // the surface's own wrap, or a click on a lower visual row resolves into the paragraph's first one.
      const lines = visualLinesOf(ta.value, this.isBlock ? width : Infinity, measure, this.softReturns);
      const row = Math.min(lines.length - 1, Math.max(0, Math.floor(local.y / (lineHeightPx || 1))));
      const line = lines[row];
      const lineAdv = measure(line.text);
      const align = this.align;
      const startX = align === 'center' ? (width - lineAdv) / 2 : align === 'right' ? width - lineAdv : 0;
      const inLine = indexAtMeasuredX(line.text, local.x - startX, measure);
      return line.start + inLine;
    } catch {
      return undefined;
    } finally {
      probe?.remove();
    }
  }

  /** Word selection for a dblclick that landed on the CANVAS over the active session (its click half opened it). */
  selectWordAt(clientX: number, clientY: number): boolean {
    const ta = this.textarea;
    const place = this.placement;
    if (!ta || !place) return false;
    const local = this.clientToLocal(clientX, clientY);
    if (!local) return false;
    ta.focus();
    if (this.shapedLayout) {
      const [from, to] = wordRangeAt(ta.value, this.shapedLayout.indexAt(local.x, local.y));
      this.selectShapedRange(from, to);
      return true;
    }

    const s = place.style;
    const surface = surfacePointFromFrame(local, place.topOffset, s.horizontalScale ?? 1, this.growShiftX, s.verticalScale ?? 1);
    const lineHeightPx = parseFloat(ta.style.lineHeight) || place.realLineHeightPx;
    const idx = this.plainIndexAt(ta, surface, lineHeightPx);
    if (idx === undefined) return false;
    const [ws, we] = wordRangeAt(ta.value, idx);
    ta.setSelectionRange(ws, we);
    return true;
  }

  /**
   * The camera moved mid-session (two-finger pan / pinch zoom): re-seat the surface on the box's fresh screen
   * quad. The local frame keeps its ENTRY coordinates — the new matrix alone carries the camera — so the text
   * metrics, the engine-fed caret grid and the clamp all stay valid without a rebuild; only the grown width's
   * alignment shift is re-applied, since it rides the base matrix.
   */
  reposition(quad: Quad): void {
    const ta = this.textarea;
    const place = this.placement;
    if (!ta || !place || this.dockHost) return;
    place.quad = quad;
    const { transform, transformOrigin, matrix } = textareaTransformFromQuad(
      quad,
      place.localWidth,
      place.localHeight,
      place.topOffset,
      place.style.horizontalScale ?? 1,
      place.style.verticalScale ?? 1,
    );
    this.baseMatrix = matrix;
    ta.style.transform = transform;
    ta.style.transformOrigin = transformOrigin;
    this.caretMatrix = textareaTransformFromQuad(quad, place.localWidth, place.localHeight).matrix;
    this.growWidth();
    this.syncShapedCaret();
    this.placeChip();
  }

  /** Selection captured at the END of the last session (commit or cancel); undefined while a session is live. */
  get lastSelection(): { start: number; end: number } | undefined {
    return this.endedSelection;
  }

  private captureEndedSelection(ta: HTMLTextAreaElement): void {
    const start = ta.selectionStart ?? 0;
    const end = ta.selectionEnd ?? 0;
    this.endedSelection = { start: Math.min(start, end), end: Math.max(start, end) };
  }

  /**
   * Read the current text, fire onCommit ONCE, tear down. Deferred while composing; `force` (host swaps the model
   * under the edit) commits the composed value now — a later compositionend would land on the next model.
   */
  commit(force = false): void {
    const ta = this.textarea;
    if (!ta || this.done) return;
    if (this.composing) {
      if (!force) {
        this.pendingCommit = true;
        return;
      }

      // The revert's value write can end the IME session synchronously — that compositionend must not commit again.
      this.pendingCommit = false;
      this.settleComposition();
    }

    // Over the cap a forced save is dropped like blur's: the server refuses it anyway (422 QUOTE_TOO_LONG).
    if (force && this.overLimit()) return void this.cancel();

    this.captureEndedSelection(ta);
    const text = ta.value;
    // An untouched session (open → blur, nothing typed) is a cancel, not a write: committing here would persist the
    // seeded value — for an empty slot that is the editor's placeholder hint, which must never become real content.
    if (text === this.initialText) {
      this.cancel();
      return;
    }

    const id = this.id as string;
    this.done = true;
    this.teardown();
    this.handlers.onCommit(id, text);
    this.handlers.onEnd?.(id);
  }

  /** Discard the edit (Escape) — no commit. */
  cancel(): void {
    const id = this.id;
    if (!this.textarea || this.done || id === undefined) return;
    this.captureEndedSelection(this.textarea);
    this.done = true;
    this.teardown();
    this.handlers.onCancel?.(id);
    this.handlers.onEnd?.(id);
  }

  destroy(): void {
    if (!this.textarea) return;
    this.done = true;
    this.teardown();
  }

  private attachHandlers(ta: HTMLTextAreaElement): void {
    ta.addEventListener('compositionstart', this.onCompositionStart);
    ta.addEventListener('compositionend', this.onCompositionEnd);
    ta.addEventListener('keydown', this.onKeyDown);
    ta.addEventListener('blur', this.onBlur);
    ta.addEventListener('beforeinput', this.onBeforeInput);
    ta.addEventListener('input', this.onInput);
    for (const ev of SCROLL_PIN_EVENTS) ta.addEventListener(ev, this.pinScroll);
    ta.addEventListener('keyup', this.notifySelection);
    ta.addEventListener('mouseup', this.notifySelection);
    ta.addEventListener('select', this.notifySelection);
    if (!this.shapedLayout) return;
    ta.addEventListener('mousedown', this.onShapedMouseDown);
    ta.addEventListener('keyup', this.onShapedSync);
    ta.addEventListener('select', this.onShapedSync);
    document.addEventListener('selectionchange', this.onShapedSync);
  }

  private notifySelection = (): void => {
    const ta = this.textarea;
    const id = this.id;
    if (!ta || this.done || id === undefined) return;
    const start = ta.selectionStart ?? 0;
    const end = ta.selectionEnd ?? 0;
    this.handlers.onSelectionChange?.(id, { start: Math.min(start, end), end: Math.max(start, end) });
  };

  private snapshot(): EditSnapshot {
    const ta = this.textarea;
    if (!ta) return { value: '', start: 0, end: 0 };
    return {
      value: ta.value,
      start: ta.selectionStart ?? ta.value.length,
      end: ta.selectionEnd ?? ta.value.length,
    };
  }

  // ==================== Product rules: cap, counter chip, ✓ ====================

  /** Counter + ✓ under the box: the cap is a product rule, so the box itself says how much room is left and
   *  offers the save Enter also does. The refusal MESSAGE belongs to every frame-clamped box instead — the
   *  photographer's frame stops the input exactly like the student's cap, and a silent stop reads as broken.
   *  Neither counter nor clamp → nothing is mounted and nothing changes. */
  private mountChip(): void {
    const withCounter = this.rules?.counter === 'remaining';
    if (!withCounter && !this.measureFit) return;
    const el = document.createElement('div');
    // Docked: the chip flows under the field in the dock, not pinned under the box on the canvas.
    el.className = `${this.dockHost ? '' : 'absolute z-20 '}flex flex-col gap-1 text-xs`;
    const message = document.createElement('div');
    message.className = 'text-destructive';
    message.textContent = LIMIT_MESSAGE;
    message.style.display = 'none';
    if (withCounter) {
      const row = document.createElement('div');
      row.className = 'flex items-center justify-between gap-2';
      const counter = document.createElement('span');
      const ok = document.createElement('button');
      ok.type = 'button';
      ok.textContent = '✓';
      ok.setAttribute('aria-label', 'Сохранить');
      row.append(counter, ok);
      el.append(row);
      ok.addEventListener('click', this.onChipCommit);
      this.chipCounterEl = counter;
      this.chipOkEl = ok;
    }

    el.append(message);
    // The pointer must never leave the textarea: its blur commits, and the commit would tear the ✓ down mid-click.
    el.addEventListener('mousedown', this.onChipMouseDown);
    this.chipEl = el;
    this.chipMessageEl = message;
    (this.dockHost ?? this.host).appendChild(el);
    this.placeChip();
    this.syncChip();
  }

  /** Under the box's bottom edge, in host px — a chip riding the box matrix would shrink with the camera until
   *  it is unreadable, while the box it belongs to stays where the text is. */
  private placeChip(): void {
    const el = this.chipEl;
    const quad = this.placement?.quad;
    if (!el || !quad || this.dockHost) return;
    const left = Math.min(quad.bl.x, quad.br.x);
    el.style.left = `${round(left)}px`;
    el.style.top = `${round(Math.max(quad.bl.y, quad.br.y) + CHIP_GAP_PX)}px`;
    // The bar spans the box: counter at its left edge, ✓ at its right — the prototype's own row.
    el.style.width = `${round(Math.max(quad.br.x, quad.tr.x) - left)}px`;
  }

  /** Entered longer than the cap (a quote written before it existed) or grown there — save is off until shortened. */
  private overLimit(): boolean {
    const max = this.rules?.maxLength ?? null;
    return max !== null && (this.textarea?.value.length ?? 0) > max;
  }

  private syncChip(): void {
    const counter = this.chipCounterEl;
    const max = this.rules?.maxLength ?? null;
    if (!counter || max === null) return;
    const length = this.textarea?.value.length ?? 0;
    const over = length > max;
    const left = over ? length - max : max - length;
    counter.textContent = `${over ? 'Превышено на' : 'Осталось'} ${left} ${charWord(left)}`;
    const alarmed = over || this.chipMessageEl?.style.display === '';
    counter.className = alarmed ? 'text-destructive' : 'text-c2';
    if (this.chipOkEl) {
      this.chipOkEl.disabled = over;
      // Gated save keeps the brand fill in its muted tone (feedback rule: never grey, never opacity).
      this.chipOkEl.className = `grid size-6 place-items-center rounded-md ${
        over ? 'border border-[var(--acc-line)] bg-[var(--acc-muted)] text-acc-muted-ink' : 'bg-primary text-primary-foreground'
      }`;
    }
  }

  /** A refused keystroke says why, then goes quiet on its own — a red that outlived its reason would read as a
   *  permanent error on text the box is perfectly happy with. */
  private showLimitMessage(): void {
    const message = this.chipMessageEl;
    if (!message) return;
    // One message per limit HIT, not per keystroke: at the border every next key is refused too, and re-arming
    // the timer on each would hold the red up for as long as the user keeps typing.
    if (this.chipMessageTimer !== undefined) return;
    message.style.display = '';
    this.chipMessageTimer = setTimeout(() => {
      message.style.display = 'none';
      this.chipMessageTimer = undefined;
      this.syncChip();
    }, LIMIT_MESSAGE_MS);
    this.syncChip();
  }

  private onChipMouseDown = (event: MouseEvent): void => {
    event.preventDefault();
  };

  private onChipCommit = (): void => {
    if (!this.overLimit()) this.commit();
  };

  /**
   * Hard cap: the input STOPS at the border. Decided here, before the value changes, so a keystroke and a paste
   * get the same answer and the message never depends on the browser's own `maxlength` truncation. Вставка из
   * буфера при этом не пропадает целиком: докладывается то, что помещается, по последнему целому слову.
   */
  private refuseAtCap(event: Event): boolean {
    const ta = this.textarea;
    if (!ta) return false;
    const input = event as InputEvent;
    const breaks = input.inputType === 'insertLineBreak' || input.inputType === 'insertParagraph';
    const inserted = input.data ?? input.dataTransfer?.getData('text') ?? (breaks ? '\n' : '');
    if (!inserted) return false; // deletions and formatting always pass — an over-long quote must stay shortenable
    const max = this.rules?.maxLength ?? null;
    // Рамку здесь меряет только многосимвольная вставка (буфер, drop): одиночная клавиша по-прежнему уходит в
    // живой откат после `input`, где её и останавливает клампа.
    const pasted = inserted.length > 1;
    if (max === null && !pasted) return false;
    const start = Math.min(ta.selectionStart ?? 0, ta.selectionEnd ?? 0);
    const end = Math.max(ta.selectionStart ?? 0, ta.selectionEnd ?? 0);
    const fits = (text: string): boolean => {
      const next = ta.value.slice(0, start) + text + ta.value.slice(end);
      if (max !== null && next.length > max) return false;
      return !pasted || !this.clampRejects(ta.value, next);
    };

    if (fits(inserted)) return false;
    event.preventDefault();
    const fitting = pasted ? fittingPrefix(inserted, fits) : '';
    if (fitting) {
      // Снимок берётся ДО вставки: живой откат сравнивает ровно с тем текстом, на котором кусок был измерен.
      this.inputSnap = this.snapshot();
      ta.setRangeText(fitting, start, end, 'end');
      ta.dispatchEvent(new Event('input', { bubbles: true }));
    }

    this.showLimitMessage();
    return true;
  }

  private unmountChip(): void {
    if (this.chipMessageTimer !== undefined) clearTimeout(this.chipMessageTimer);
    this.chipMessageTimer = undefined;
    this.chipOkEl?.removeEventListener('click', this.onChipCommit);
    this.chipEl?.removeEventListener('mousedown', this.onChipMouseDown);
    this.chipEl?.remove();
    this.chipEl = undefined;
    this.chipCounterEl = undefined;
    this.chipOkEl = undefined;
    this.chipMessageEl = undefined;
    this.rules = undefined;
  }

  // `beforeinput` fires while the textarea still holds the pre-edit text — the state a rejected edit reverts to.
  private onBeforeInput = (event: Event): void => {
    if (this.composing) return;
    if (this.refuseAtCap(event)) return;
    if (!this.measureFit) return;
    this.inputSnap = this.snapshot();
  };

  private onCompositionStart = (): void => {
    this.composing = true;
    // Fires before the first composition mutation — the whole session reverts to this text as one unit.
    if (this.measureFit) this.compositionSnap = this.snapshot();
  };

  private onCompositionEnd = (): void => {
    this.settleComposition();
    if (this.pendingCommit) this.commit();
  };

  /** Ends the IME session: compositionend and a forced commit mid-composition both land the text through here. */
  private settleComposition(): void {
    this.composing = false;
    // The whole IME session reverts as one unit — a mid-composition revert would detach the candidate window.
    const reverted = this.revertIfOverflowing(this.compositionSnap);
    this.growWidth(); // deferred while composing — a mid-composition reflow can detach the IME candidate window
    if (reverted) {
      // The composing input events already published the candidate and may have grown the box: republish the
      // restored text and re-fit, else the engine keeps drawing the rejected candidate until the next keystroke.
      this.growHeight();
      // No `input` event follows a programmatic revert, so the shaped grid is rebuilt here instead.
      this.refreshShapedCaret();
      if (this.id !== undefined && this.textarea) this.handlers.onInput?.(this.id, this.textarea.value);
    }
  }

  private onKeyDown = (event: KeyboardEvent): void => {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.cancel();
      return;
    }

    // Rules host (student quote): Enter is the save, Shift+Enter still breaks the line. Over the cap the save is
    // simply not offered — the same refusal the ✓ shows, so the two entries never disagree.
    if (this.rules?.commitOnEnter && event.key === 'Enter' && !event.shiftKey && !event.isComposing) {
      event.preventDefault();
      if (!this.overLimit()) this.commit();
      return;
    }

    // Photoshop paragraphs: Shift+Enter is a soft return (U+2028) — a line inside the paragraph, no spacing, no indent.
    if (this.softReturns && event.key === 'Enter' && event.shiftKey && !event.isComposing && this.textarea) {
      event.preventDefault();
      this.insertSoftReturn(this.textarea);
      return;
    }

    this.onShapedKeyDown(event);
    // Enter inserts a newline (default) — block-mode text commits only on blur/Escape.
  };

  private onBlur = (): void => {
    // Over the cap a click outside DISCARDS the edit instead of writing it: Enter and ✓ both refuse to
    // save such text, and the server refuses it too (422 QUOTE_TOO_LONG) — committing here would only
    // trade the same loss for a confusing error. Blur cannot keep the session open the way Enter does:
    // focus has already left, so there is nowhere to shorten the text.
    if (this.overLimit()) return void this.cancel();
    this.commit();
  };

  /** Types U+2028 over the selection through the input path a keystroke takes (cap, live render, undo snapshot). */
  private insertSoftReturn(ta: HTMLTextAreaElement): void {
    const start = Math.min(ta.selectionStart ?? 0, ta.selectionEnd ?? 0);
    const end = Math.max(ta.selectionStart ?? 0, ta.selectionEnd ?? 0);
    const max = ta.maxLength > 0 ? ta.maxLength : null;
    if (max !== null && ta.value.length - (end - start) + 1 > max) return;
    this.inputSnap = this.snapshot();
    ta.setRangeText(SOFT_LINE_BREAK, start, end, 'end');
    ta.dispatchEvent(new Event('input', { bubbles: true }));
  }

  private onInput = (): void => {
    const reverted = this.composing ? false : this.revertIfOverflowing(this.inputSnap);
    this.reshiftIfLineCountChanged();
    this.growWidth();
    this.growHeight();
    this.pinScroll();
    // The text changed → the engine re-wraps it, so the caret grid must be rebuilt before it is read.
    // Strictly AFTER the clamp's revert: the grid has to describe the text that actually stayed in the box,
    // otherwise a rejected keystroke would leave the caret standing on glyphs the engine never drew.
    this.refreshShapedCaret();
    this.syncChip();

    if (!reverted && this.id !== undefined && this.textarea) this.handlers.onInput?.(this.id, this.textarea.value);
  };

  /** The frame is the maximum: anything the ENGINE lays out past it reverts to the snapshot; text that already
   *  overflowed on entry (imported overflow, a shrunk frame) can still be shortened. Returns true when reverted. */
  private revertIfOverflowing(snap: EditSnapshot): boolean {
    const ta = this.textarea;
    if (!ta || !this.clampRejects(snap.value, ta.value)) return false;
    ta.value = snap.value;
    ta.setSelectionRange(snap.start, snap.end);
    this.showLimitMessage();
    return true;
  }

  /** Вердикт клампы по кандидату: тот же предикат для живого отката и для подбора куска вставки. */
  private clampRejects(before: string, after: string): boolean {
    const measureFit = this.measureFit;
    if (!measureFit || after === before) return false;
    const fit = measureFit(after);
    if (fitsFrame(fit)) return false;
    // Past the frame. The only pass is a measured retreat on BOTH axes — an over-full box stays shrinkable because
    // removing text lowers or holds both numbers. Neither a shorter string nor a deletion earns a pass on its own:
    // fewer but wider glyphs (`iiiiiiii` → `WWWW`) grow the line, and Backspace on a line break joins two fitting
    // lines into one that overruns the frame width. Only the SPILL is compared, never the signed margin: a block
    // that reflows after a deletion can hand its slack to a still-fitting line, and a wider line that is inside the
    // frame is not an overflow to defend against.
    const was = measureFit(before);
    const worse = spill(fit.heightOver) > spill(was.heightOver) || spill(fit.widthOver) > spill(was.widthOver);
    // A box that ALREADY spills grows invisibly under the spill test: neither number moves for an insertion that
    // adds no ink below the last line and pushes no line past the wrap budget, so keystroke after keystroke was
    // accepted, the engine clipped the glyphs at the frame — and the caret, drawn from the engine grid into the
    // unclipped overlay host, marched out past the frame's edge (FKS-12432). Over the frame the box only shrinks:
    // growth is refused outright, and shortening (deletion, a shorter replacement) keeps its pass.
    const spilling = spill(was.heightOver) > 0 || spill(was.widthOver) > 0;
    return worse || (spilling && after.length > before.length);
  }

  // Content width with the align pinned left — a right/center line's leftward overhang is invisible to scrollWidth.
  private measureContentWidth(): number {
    const ta = this.textarea;
    if (!ta) return 0;
    const prevWidth = ta.style.width;
    ta.style.textAlign = 'left';
    ta.style.width = '0px';
    const content = ta.scrollWidth;
    ta.style.textAlign = this.align;
    ta.style.width = prevWidth;
    return content;
  }

  // ==================== Shaped (area-contour) caret ====================

  /** Rebuilds the engine caret grid for the text the textarea holds RIGHT NOW (every edit re-wraps it). No-op
   *  outside a shaped session, so the clamp and the plain-caret path can call it unconditionally. */
  private refreshShapedCaret(): void {
    if (!this.shapedSource || !this.textarea) return;
    this.shapedLayout = this.shapedSource(this.textarea.value);
    this.caretGoalX = undefined;
    this.clipSurfaceToText();
    this.syncShapedCaret();
  }

  // Clicks only along the letters: one away from them reaches what lies beneath and ends the session.
  private clipSurfaceToText(): void {
    const ta = this.textarea;
    const area = this.fixedSurface ? this.shapedLayout?.hitArea : undefined;
    if (!ta) return;
    const s = this.placement?.style;
    const h = s?.horizontalScale || 1;
    const v = s?.verticalScale || 1;
    const outline = (area ?? [])
      .map(poly => `${poly.map((p, i) => `${i ? 'L' : 'M'} ${round(p.x / h)} ${round(p.y / v)}`).join(' ')} Z`)
      .join(' ');
    ta.style.clipPath = outline ? `path('${outline}')` : '';
  }

  private mountShapedCaret(color: string): void {
    const el = document.createElement('div');
    Object.assign(el.style, {
      position: 'absolute',
      left: '0px',
      top: '0px',
      background: color,
      pointerEvents: 'none',
      transformOrigin: '0 0',
      // Above the textarea (z 15) so a selection highlight can never bury it.
      zIndex: '16',
    });
    this.caretEl = el;
    this.host.appendChild(el);
    this.caretBlinkTimer = setInterval(() => {
      if (this.caretEl) this.caretEl.style.opacity = this.caretEl.style.opacity === '0' ? '1' : '0';
    }, SHAPED_CARET_BLINK_MS);
  }

  /** Client px → the box's local px (the space the shaped grid reports), through the inverse quad matrix. */
  private clientToLocal(clientX: number, clientY: number): { x: number; y: number } | undefined {
    const inverse = invertAffine(this.caretMatrix);
    if (!inverse) return undefined;
    const host = this.host.getBoundingClientRect();
    return applyAffine(inverse, clientX - host.left, clientY - host.top);
  }

  private caretIndex(): number {
    const ta = this.textarea;
    if (!ta) return 0;
    // The moving end of a selection is where the caret is drawn.
    return (ta.selectionDirection === 'backward' ? ta.selectionStart : ta.selectionEnd) ?? 0;
  }

  private setCaretIndex(index: number): void {
    this.selectionAnchor = index;
    this.textarea?.setSelectionRange(index, index);
  }

  /** Selection with engine-true endpoints. The browser still PAINTS the highlight from its rectangular
   * layout (pre-existing textarea limitation), but the indices — what typing replaces — follow the glyphs. */
  private selectShapedRange(anchor: number, focus: number): void {
    const ta = this.textarea;
    if (!ta) return;
    this.selectionAnchor = anchor;
    if (anchor <= focus) ta.setSelectionRange(anchor, focus, 'forward');
    else ta.setSelectionRange(focus, anchor, 'backward');
    this.syncShapedCaret();
  }

  private onShapedMouseDown = (event: MouseEvent): void => {
    const ta = this.textarea;
    const layout = this.shapedLayout;
    if (!ta || !layout || event.button !== 0) return;
    const local = this.clientToLocal(event.clientX, event.clientY);
    if (!local) return;
    // The browser would place the caret AND grow selections from ITS rectangular layout — own the gesture fully.
    event.preventDefault();
    ta.focus();
    this.caretGoalX = undefined;
    const index = layout.indexAt(local.x, local.y);
    if (event.detail >= 3) {
      this.selectShapedRange(layout.lineEdge(index, 'start'), layout.lineEdge(index, 'end'));
      return;
    }

    if (event.detail === 2) {
      const [from, to] = wordRangeAt(ta.value, index);
      this.selectShapedRange(from, to);
      return;
    }

    if (event.shiftKey) {
      this.selectShapedRange(this.selectionAnchor ?? index, index);
      return;
    }

    this.setCaretIndex(index);
    this.syncShapedCaret();
    document.addEventListener('mousemove', this.onShapedDragMove);
    document.addEventListener('mouseup', this.onShapedDragEnd);
  };

  private onShapedDragMove = (event: MouseEvent): void => {
    const layout = this.shapedLayout;
    const anchor = this.selectionAnchor;
    if (!layout || anchor === undefined) return;
    const local = this.clientToLocal(event.clientX, event.clientY);
    if (!local) return;
    this.selectShapedRange(anchor, layout.indexAt(local.x, local.y));
  };

  private onShapedDragEnd = (): void => {
    document.removeEventListener('mousemove', this.onShapedDragMove);
    document.removeEventListener('mouseup', this.onShapedDragEnd);
  };

  private onShapedSync = (): void => {
    this.syncShapedCaret();
  };

  private onShapedKeyDown(event: KeyboardEvent): void {
    const layout = this.shapedLayout;
    // IME composition owns arrows/Home/End (candidate navigation) — remapping them mid-composition breaks input.
    if (this.composing || event.isComposing) return;
    if (!layout || event.altKey || event.ctrlKey) return;
    const index = this.caretIndex();
    const length = this.textarea?.value.length ?? 0;
    // Shift-extension moves only the FOCUS end; the anchor is the gesture's own (engine-true), because the
    // browser's anchor lives in its rectangular layout and would extend over other glyphs on vertical moves.
    const place = (next: number): void => {
      if (event.shiftKey) this.selectShapedRange(this.selectionAnchor ?? index, next);
      else this.setCaretIndex(next);
    };

    if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
      event.preventDefault();
      const delta = event.key === 'ArrowUp' ? -1 : 1;
      // Survives the keyup sync on purpose: a run of ↑/↓ must keep the column it started from, so only a
      // horizontal move / click / edit clears it.
      const goal = this.caretGoalX ?? layout.slotAt(index)?.x;
      const next = layout.indexByLineStep(index, delta, goal);
      // Past the first/last line the browser jumps to the text edge — keep that.
      place(next ?? (delta < 0 ? 0 : length));
      this.caretGoalX = goal;
      this.syncShapedCaret();
      return;
    }

    // Home/End (and their macOS ⌘←/⌘→ twins) must walk the ENGINE's visual line, not the browser's.
    const toStart = event.key === 'Home' || (event.metaKey && event.key === 'ArrowLeft');
    const toEnd = event.key === 'End' || (event.metaKey && event.key === 'ArrowRight');
    if (toStart || toEnd) {
      event.preventDefault();
      this.caretGoalX = undefined;
      place(layout.lineEdge(index, toStart ? 'start' : 'end'));
      this.syncShapedCaret();
      return;
    }

    if (event.shiftKey) return;

    // ←/→ and typing move the index linearly — layout-independent, so the browser keeps them; only the
    // drawn caret has to catch up once the default action has run.
    if (!event.metaKey) {
      this.caretGoalX = undefined;
      queueMicrotask(this.onShapedSync);
    }
  }

  private syncShapedCaret(): void {
    const el = this.caretEl;
    const layout = this.shapedLayout;
    if (!el) return;
    this.syncShapedBands();
    const slot = layout?.slotAt(this.caretIndex());
    if (!slot) {
      el.style.display = 'none';
      return;
    }

    const [a, b, c, d, tx, ty] = this.caretMatrix;
    const angle = slot.angle ?? 0;
    const cos = Math.cos(angle);
    const sin = Math.sin(angle);
    // Keep the on-screen thickness constant whatever scale the box carries — along the caret's own (turned) cross axis.
    const width = SHAPED_CARET_WIDTH_PX / (Math.hypot(a * cos + c * sin, b * cos + d * sin) || 1);
    el.style.display = '';
    el.style.width = `${round(width)}px`;
    el.style.height = `${round(Math.max(1, slot.height))}px`;
    const seat = `translate(${round(slot.x)}px, ${round(slot.top)}px)${turn(angle)}`;
    el.style.transform = `matrix(${[a, b, c, d, tx, ty].join(', ')}) ${seat}`;
    el.style.opacity = '1'; // a moving caret is always solid — the blink restarts from visible
  }

  /** Highlight bands for the current selection, on the engine's grid. Reuses the divs across syncs (no churn). */
  private syncShapedBands(): void {
    const ta = this.textarea;
    const layout = this.shapedLayout;
    if (!ta) return;
    const from = ta.selectionStart ?? 0;
    const to = ta.selectionEnd ?? 0;
    const bands = layout && to > from ? layout.bandsFor(from, to) : [];
    const [a, b, c, d, tx, ty] = this.caretMatrix;
    for (let i = 0; i < bands.length; i++) {
      let el = this.bandEls[i];
      if (!el) {
        el = document.createElement('div');
        Object.assign(el.style, {
          position: 'absolute',
          left: '0px',
          top: '0px',
          background: SHAPED_BAND_COLOR,
          pointerEvents: 'none',
          transformOrigin: '0 0',
          // Under the textarea (z 15) and the caret (16) — the highlight never buries either.
          zIndex: '14',
        });
        this.bandEls[i] = el;
        this.host.appendChild(el);
      }

      const band = bands[i];
      el.style.display = '';
      // Cut to the bend of a warped line, in the band's own px.
      el.style.clipPath = band.outline
        ? `polygon(${band.outline.map(p => `${round(p.x - band.x)}px ${round(p.y - band.top)}px`).join(', ')})`
        : '';
      el.style.width = `${round(Math.max(0, band.width))}px`;
      el.style.height = `${round(Math.max(1, band.height))}px`;
      const seat = `translate(${round(band.x)}px, ${round(band.top)}px)${turn(band.angle ?? 0)}`;
      el.style.transform = `matrix(${[a, b, c, d, tx, ty].join(', ')}) ${seat}`;
    }

    for (let i = bands.length; i < this.bandEls.length; i++) this.bandEls[i].style.display = 'none';
  }

  private unmountShapedCaret(): void {
    if (this.caretBlinkTimer !== undefined) clearInterval(this.caretBlinkTimer);
    this.caretBlinkTimer = undefined;
    this.caretEl?.remove();
    this.caretEl = undefined;
    for (const el of this.bandEls) el.remove();
    this.bandEls = [];
    this.shapedLayout = undefined;
    this.shapedSource = undefined;
    this.caretGoalX = undefined;
    this.selectionAnchor = undefined;
    document.removeEventListener('selectionchange', this.onShapedSync);
    document.removeEventListener('mousemove', this.onShapedDragMove);
    document.removeEventListener('mouseup', this.onShapedDragEnd);
  }

  private pinScroll = (): void => {
    const ta = this.textarea;
    // A docked field is a real input: a long text scrolls inside it.
    if (!ta || this.dockHost) return;
    if (ta.scrollTop !== 0) ta.scrollTop = 0;
    if (ta.scrollLeft !== 0) ta.scrollLeft = 0;
  };

  /** Centred/bottom text re-centres when a line is added or removed — the only input that moves the engine's shift.
   *  A single-line-locked session also swaps its caret metrics here: the compressed cap→baseline line box is right
   *  only while the buffer holds ONE line — from the second line on, the browser must advance by the engine's real
   *  leading or the caret sits above the drawn glyphs on every line after the first (and back on deletion). */
  private reshiftIfLineCountChanged(): void {
    const ta = this.textarea;
    const place = this.placement;
    if (!ta || !place || this.fixedSurface || this.dockHost) return;
    if (!this.entrySingleLineLocked && !this.verticalShift) return;
    // Only block text wraps — a free session counts authored breaks and skips the engine probe.
    const lines = this.isBlock ? this.laidOutLines(ta.value) : countLines(ta.value, this.softReturns);
    if (lines === this.shiftLineCount) return;
    this.shiftLineCount = lines;
    const s = place.style;
    if (this.entrySingleLineLocked) {
      const lineHeightPx = lines > 1 ? place.realLineHeightPx : this.entryLineHeightPx;
      ta.style.lineHeight = `${lineHeightPx}px`;
      this.baseTopOffset = firstLineOffsetOf(s, lineHeightPx);
    }

    ta.style.height = `${caretClipHeight(
      this.baseTopOffset,
      s.fontSizePx,
      // Surface px, exactly as enter() sized it — the raw frame height collapsed the clip under a vertical scale.
      place.localHeight / (s.verticalScale || 1),
      s.engineCapPx,
      s.ascenderDeltaPx,
      s.block,
      lines,
      place.realLineHeightPx,
      s.verticalScale,
    )}px`;
    const topOffset = this.baseTopOffset - (this.verticalShift?.(ta.value) ?? 0);
    place.topOffset = topOffset;
    const { matrix } = textareaTransformFromQuad(
      place.quad,
      place.localWidth,
      place.localHeight,
      topOffset,
      place.style.horizontalScale ?? 1,
      place.style.verticalScale ?? 1,
    );
    this.baseMatrix = matrix;
    ta.style.transform = `matrix(${matrix.map(round).join(', ')})`;
  }

  private growHeight(): void {
    const ta = this.textarea;
    if (!ta || this.fixedSurface) return;
    const prev = ta.style.height;
    ta.style.height = 'auto';
    const next = `${ta.scrollHeight}px`;
    ta.style.height = next === '0px' ? prev : next;
  }

  // Free text only: block text wraps inside its frame, so its width never grows. Content is measured with the
  // align pinned left — a right/center line's leftward overhang is unscrollable in LTR and invisible to scrollWidth.
  // Deferred during IME composition: the width/align measurement reflow would move the candidate window mid-word.
  private growWidth(): void {
    const ta = this.textarea;
    if (!ta || this.isBlock || this.composing || this.fixedSurface || this.dockHost) return;
    // The engine's own measure when the host wires it: expressed as a share of the frame, so the surface's scales
    // cancel out and no assumption about glyph stretch or view scale has to be re-derived here.
    const cached = this.growRatioCache?.text === ta.value ? this.growRatioCache.ratio : undefined;
    const ratio = cached ?? this.contentWidthRatio?.(ta.value);
    if (ratio !== undefined && cached === undefined) this.growRatioCache = { text: ta.value, ratio };
    const content = ratio !== undefined && ratio > 0 ? this.baseWidth * ratio : this.measureContentWidth();
    // An EMPTIED buffer legitimately measures 0 and resets home; only a failed measure of real text bails out.
    if (!content && ta.value) return; // jsdom / detached — keep the entry geometry

    const { width, shiftX } = grownWidthLayout(this.align, this.baseWidth, content + GROW_CARET_SLACK_PX);
    const [a, b, c, d, tx, ty] = this.baseMatrix;
    this.growShiftX = shiftX;
    ta.style.width = `${width}px`;
    ta.style.transform = `matrix(${[a, b, c, d, tx + shiftX * a, ty + shiftX * b].map(round).join(', ')})`;
  }

  private teardown(): void {
    const ta = this.textarea;
    this.textarea = undefined;
    this.id = undefined;
    this.dockHost = undefined;
    this.unmountShapedCaret();
    this.unmountChip();
    if (!ta) return;
    ta.removeEventListener('compositionstart', this.onCompositionStart);
    ta.removeEventListener('compositionend', this.onCompositionEnd);
    ta.removeEventListener('keydown', this.onKeyDown);
    ta.removeEventListener('blur', this.onBlur);
    ta.removeEventListener('beforeinput', this.onBeforeInput);
    ta.removeEventListener('input', this.onInput);
    ta.removeEventListener('mousedown', this.onShapedMouseDown);
    ta.removeEventListener('keyup', this.onShapedSync);
    ta.removeEventListener('select', this.onShapedSync);
    ta.removeEventListener('keyup', this.notifySelection);
    ta.removeEventListener('mouseup', this.notifySelection);
    ta.removeEventListener('select', this.notifySelection);
    for (const ev of SCROLL_PIN_EVENTS) ta.removeEventListener(ev, this.pinScroll);
    ta.remove();
  }
}
